import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {
    clearLiveLangCache,
    flushQueue,
    hasQueueWord,
    setDebugMode,
    setHashIfNeeded,
    setLiveLang,
    t as translateFn
} from "./LiveDictionary";
import {getLocaleFromLocal, setLocaleToLocal} from "../core/localization/localizationHelper";
import {LocaleType} from "../core/localization/intertace";
import {isArrayHaveItem} from "../helpers/listHelper";
import {stringFormat} from "../helpers/stringHelper";
import {useSelector} from "react-redux";
import {RootState} from "../redux/store";
import {useTimer} from "react-timer-hook";
import dayjs, {Dayjs} from "dayjs";
import {getUuid} from "../helpers/uuidHelper";

interface I18nContextType {
    tlocale: LocaleType;
    mhash: string,
    lhash:string,
    clearcache: (lang?: string) => void;
    t: (key: string, vars?: Record<string, string>) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const {locale} = useSelector(({localization}: RootState) => localization);
    const [localeState, setLocaleState] = useState<LocaleType>(() => getLocaleFromLocal());
    const [expiryTimestamp, setExpiryTimestamp] = useState<Date>(dayjs().add(100, "milliseconds").toDate());
    const [finishDate, setFinishDate] = useState<Dayjs>();
    const [hash, setHash] = useState<string>(getUuid());
    const [langHash, setLangHash] = useState<string>();
    const {lastCheckTime} = useSelector(({auth}: RootState) => auth);
    const timer = useTimer({
        expiryTimestamp,
        autoStart: true,
        onExpire: () => {
            flushQueue().then(
                value => {
                    if (value) {
                        setHash(getUuid())
                    }
                }
            ).catch(reason => {
                console.log("I8n timer->");
                console.log(reason);
            }).finally(() => {
                setFinishDate(dayjs());
            })


        },
    });

    useEffect(() => {
        const tempData = finishDate === undefined ? dayjs().add(250, "milliseconds").toDate() : finishDate.add(250, "milliseconds").toDate();
        timer.restart(tempData);
        setExpiryTimestamp(tempData);

    }, [hasQueueWord, setExpiryTimestamp, finishDate]);
    useEffect(() => {
        setLocaleState(locale);
        setLiveLang(locale);
        setLocaleToLocal(locale);
        setHashIfNeeded(locale, lastCheckTime !== 0).then(
            value => {
                if (value.change) {
                    setHash(getUuid())
                }
                setLangHash((s) => {
                    if (s !== undefined && s === value.langHash) {
                        return value.langHash;
                    } else {
                        return s;
                    }
                });
            }
        ).catch(reason => {
            console.log("I8n timer->");
            console.log(reason);
        })

    }, [locale, lastCheckTime]);


    useEffect(() => {
        setDebugMode(false);
    }, []);

    const t = useCallback((key: string, params?: Record<string, string>) => {
        let val = translateFn(key);
        if (params !== undefined && isArrayHaveItem(Object.keys(params))) {
            val = stringFormat(val, params);
        }
        return val;
    }, []);

    const clearCache = useCallback((lang?: string) => {
        clearLiveLangCache(lang);
        setHash(getUuid())
    }, [])
    const contextValue = useMemo(() => {
        return {
            tlocale: localeState,
            t,
            mhash: hash,
            lhash:langHash,
            clearcache: clearCache
        } as I18nContextType
    }, [localeState, t, hash, clearCache]);
    return (
        <I18nContext.Provider value={contextValue}>
            {children}
        </I18nContext.Provider>
    );
};

export const useTranslation = () => {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error("useTranslation must be used within I18nProvider");
    return ctx;
};